import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PremiumProvidersPage from './pages/PremiumProvidersPage';
import PartnerDirectoryPage from './pages/PartnerDirectoryPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import FindIdPage from './pages/FindIdPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ClassDetailPage from './pages/ClassDetailPage';
import ClassBoardPage from './pages/ClassBoardPage';
import ProviderDetailPage from './pages/ProviderDetailPage';
import TermsAgreementPage from './pages/TermsAgreementPage';
import EmailVerificationPendingPage from './pages/EmailVerificationPendingPage';
import EmailVerifiedPage from './pages/EmailVerifiedPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ProviderPremiumPage from './pages/ProviderPremiumPage';
import ProviderRecruitmentPage from './pages/ProviderRecruitmentPage';
import ResumePage from './pages/ResumePage';
import ProviderPostsPage from './pages/ProviderPostsPage';
import ProviderClassCreatePage from './pages/ProviderClassCreatePage';
import ProviderPastClassCreatePage from './pages/ProviderPastClassCreatePage';
import ProviderClassEditPage from './pages/ProviderClassEditPage';
import CertificateApprovalPage from './pages/CertificateApprovalPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import MyEnrollmentsPage from './pages/MyEnrollmentsPage';
import MyFavoritesPage from './pages/MyFavoritesPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import MyCertificatesPage from './pages/MyCertificatesPage';
import CertificateViewerPage from './pages/CertificateViewerPage';
import CertificateVerifyPage from './pages/CertificateVerifyPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailPage from './pages/PaymentFailPage';
import {
  TermsOfServicePage,
  PrivacyPolicyPage,
  DataCollectionConsentPage,
  RefundPolicyPage,
} from './pages/legal';
import { useAuthStore } from './stores/authStore';

// 소셜 로그인 최초 가입 시 약관 동의·필수 정보 입력을 마치지 않으면
// (Supabase가 OAuth 완료 즉시 계정을 만들어버리므로) 다른 화면으로 못 벗어나게 강제 이동시킨다.
const TERMS_GUARD_EXEMPT_PATHS = ['/terms-agreement', '/auth/callback', '/login'];

function TermsAgreementGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (user.terms_agreed) return;
    if (TERMS_GUARD_EXEMPT_PATHS.includes(location.pathname)) return;

    const params = new URLSearchParams({
      user_id: user.id,
      provider: user.provider,
      name: user.name || '',
      email: user.email || '',
    });
    navigate(`/terms-agreement?${params.toString()}`, { replace: true });
  }, [isAuthenticated, user, location.pathname, navigate]);

  return null;
}

export default function App() {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <HelmetProvider>
      <TermsAgreementGuard />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/premium-providers" element={<PremiumProvidersPage />} />
          <Route path="/classes/board/:category" element={<ClassBoardPage />} />
          <Route path="/classes/:id" element={<ClassDetailPage />} />
          <Route path="/providers/:id" element={<ProviderDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/find-id" element={<FindIdPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/refund" element={<RefundPolicyPage />} />
          <Route path="/legal/data-collection" element={<DataCollectionConsentPage />} />
        </Route>
        {/* 프로필/관리자/교육 기관/기업 회원 페이지는 자체 레이아웃 사용 (사이드바 포함) */}
        {/* 소개 > 입점 교육 기관 / 입점 채용 기관 (자체 Header/Footer 렌더) */}
        <Route path="/partners/education" element={<PartnerDirectoryPage kind="education" />} />
        <Route path="/partners/recruitment" element={<PartnerDirectoryPage kind="recruitment" />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/enrollments" element={<MyEnrollmentsPage />} />
        <Route path="/profile/favorites" element={<MyFavoritesPage />} />
        <Route path="/profile/applications" element={<MyApplicationsPage />} />
        <Route path="/profile/certificates" element={<MyCertificatesPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/fail" element={<PaymentFailPage />} />
        <Route path="/certificates/:id/view" element={<CertificateViewerPage />} />
        <Route path="/verify/:code" element={<CertificateVerifyPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:conversationId" element={<ChatPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="/profile/resume" element={<ResumePage />} />
        <Route path="/provider/premium" element={<ProviderPremiumPage />} />
        {/* 채용 페이지는 기업 회원(employer) 전용으로 이동 — 기존 경로는 리다이렉트 */}
        <Route path="/provider/recruitment" element={<Navigate to="/employer/recruitment" replace />} />
        <Route path="/employer/recruitment" element={<ProviderRecruitmentPage />} />
        <Route path="/provider/posts" element={<ProviderPostsPage />} />
        <Route path="/provider/classes/create" element={<ProviderClassCreatePage />} />
        <Route path="/provider/classes/create-past" element={<ProviderPastClassCreatePage />} />
        <Route path="/provider/classes/:id/edit" element={<ProviderClassEditPage />} />
        <Route path="/provider/classes/:classId/certificates" element={<CertificateApprovalPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/terms-agreement" element={<TermsAgreementPage />} />
        <Route path="/email-verification-pending" element={<EmailVerificationPendingPage />} />
        <Route path="/auth/verify-email" element={<EmailVerifiedPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    </HelmetProvider>
  );
}
